import { FieldProps } from 'formik';
import { GroupBase } from 'react-select';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import {
  ErrorMessageWrapper,
  RelativeDiv,
  SelectWrapper,
  StyledReactSelect,
} from './Select.styled';
import { useTranslation } from 'react-i18next';
import ClearValueButton from 'components/ClearValueButton/ClearValueButton';

interface Option {
  label: string;
  value: string;
}

interface CustomSelectProps extends FieldProps {
  width?: string;
  height?: string;
  options: Array<Option>;
  isMulti: boolean;
  className?: string;
  placeholder?: string;
  selectedValue?: string;
  onSelect?: (option: any) => void;
  onInputChange?: (value: string) => void;
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  errorMessage: string;
  disabled: boolean;
  components?:
    | Partial<SelectComponents<unknown, boolean, GroupBase<unknown>>>
    | undefined;
  pwId?: string;
  onMenuScrollToBottom?: () => void;
  translate?: boolean;
  hasImages?: boolean;
  menuPlacement?: string;
  isError?: boolean;
  useFilterOption?: boolean;
  styles?: any;
  // Only worth setting to false: it drops react-select's own clear on a multi
  // select, which is on by default.
  isClearable?: boolean;
  showClear?: boolean;
  // Hold the clear button's room even while it is not showing, so the dropdown
  // caret keeps its place when the field gains a value.
  reserveClear?: boolean;
  onClear?: () => void;
}

export const CustomSelect = ({
  width = '200rem',
  height = '40rem',
  className,
  placeholder,
  field,
  form,
  options,
  isMulti,
  selectedValue,
  onSelect,
  onInputChange,
  onMenuClose,
  onMenuOpen,
  errorMessage,
  isError,
  disabled,
  components,
  pwId,
  onMenuScrollToBottom,
  translate = true,
  hasImages,
  menuPlacement = 'auto',
  useFilterOption = false,
  styles,
  isClearable,
  showClear = false,
  reserveClear = false,
  onClear,
}: CustomSelectProps) => {
  const { t } = useTranslation();

  /**
   * Custom filter used when `useFilterOption` is true.
   * Matches both the raw option label (usually English) and its translated label
   * so that users can find options by typing either language.
   */
  const filterOption = (
    candidate: { label: string; value: string },
    rawInput: string
  ) => {
    if (!rawInput) return true;

    const baseLabel = (candidate.label || '').toString();
    const translatedLabel = translate ? t(baseLabel) : baseLabel;
    const input = rawInput.toLowerCase();

    return (
      baseLabel.toLowerCase().includes(input) ||
      translatedLabel.toLowerCase().includes(input)
    );
  };

  const onChange = (option: Option | Option[]) => {
    onSelect && onSelect(option);
    form.setFieldValue(
      field.name,
      isMulti
        ? (option as Option[]).map((item: Option) => item.value)
        : (option as Option).value
    );
  };

  const getValue = () => {
    if (options) {
      if (isMulti) {
        return options.filter(
          (option: Option) => field && field?.value?.indexOf(option?.value) >= 0
        );
      } else {
        const existingOption = options.find(
          (option: Option) =>
            option.value === field.value ||
            option.value === selectedValue?.toString()
        );
        if (existingOption) {
          return existingOption;
        } else {
          if (form.values[field.name]) {
            // If an option has been selected, but has been removed from total options, remove the value from formik
            form.setFieldValue(field.name, undefined);
          }
          return null; // Resets the displayed value in the select if option has been removed
        }
      }
    } else {
      return isMulti ? [] : ('' as string);
    }
  };

  const formatOptionLabel = ({ label }: any) => {
    return (
      <div style={{ display: 'flex' }}>{translate ? t(label) : label}</div>
    );
  };

  return (
    <SelectWrapper styles={styles}>
      <RelativeDiv data-testid={pwId}>
        <StyledReactSelect
          width={width}
          height={height}
          hasClear={showClear || reserveClear}
          className={className}
          formatOptionLabel={formatOptionLabel}
          defaultValue={getValue()}
          name={field.name}
          value={getValue()}
          onChange={onChange}
          classNamePrefix="select"
          // menuIsOpen={true}
          placeholder={placeholder}
          options={options}
          isMulti={isMulti}
          // Our own clear button replaces react-select's. Without this a multi
          // select whose dropdown type supplies a ClearIndicator (the colour
          // and image types do) would render both, stacked on each other:
          // isClearable defaults to isMulti.
          isClearable={showClear ? false : isClearable}
          menuPlacement={menuPlacement}
          isDisabled={disabled}
          isError={
            (errorMessage !== '' && typeof errorMessage === 'string') || isError
          }
          filterOption={useFilterOption ? filterOption : undefined}
          onInputChange={(value: any) => {
            onInputChange && onInputChange(value);
          }}
          components={components}
          onMenuClose={() => {
            onMenuClose && onMenuClose();
          }}
          onMenuOpen={() => {
            onMenuOpen && onMenuOpen();
          }}
          onMenuScrollToBottom={onMenuScrollToBottom}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
        {showClear && onClear && (
          <ClearValueButton
            onClear={onClear}
            pwId={pwId ? pwId + '_clear' : undefined}
            controlHeight={height}
          />
        )}
        <ErrorMessageWrapper hasImages={hasImages}>
          {errorMessage}
        </ErrorMessageWrapper>
      </RelativeDiv>
    </SelectWrapper>
  );
};

export default CustomSelect;
